/**
 * Group header for one member order of a mixed order. Clicking it collapses/expands that member's
 * content; the owner keeps the expanded state.
 * Pass `columnsCount` to render it as a full-width row inside a Table grid, or omit it to render a
 * standalone block above a card section.
 */
import { observer } from 'mobx-react-lite';
import React from 'react';
import FontBody2 from '../../../components/fonts/font-body2/font-body2';
import FontBody3 from '../../../components/fonts/font-body3/font-body3';
import { FONT_COLOR, FONT_VARIANT } from '../../../components/fonts/font-props';
import IconNext from '../../../components/icons/icon-next/icon-next';
import IconPackage from '../../../components/icons/icon-package/icon-package';
import Icon from '../../../components/icons/icon/icon';
import useDashboardTranslation from '../../../dashboard-translation/hooks/dashboard-translation-hook';
import type { CreateRmaDraftStore } from '../stores/create-rma-draft-store';
import FulfillmentSectionMetadata from './fulfillment-section-header-metadata';
import './fulfillment-section-header.scss';

const PAGE_SCOPE = 'dashboard-create-rma';

export interface IFulfillmentSectionHeaderProps {
    draft: CreateRmaDraftStore;
    isMixedOrder: boolean;
    isExpanded: boolean;
    onToggle: () => void;
    columnsCount?: number;
}

const FulfillmentSectionHeader: React.FC<IFulfillmentSectionHeaderProps> = observer(({
    draft,
    isMixedOrder,
    columnsCount,
    isExpanded,
    onToggle,
}) => {
    const { getTranslation } = useDashboardTranslation();

    if (!isMixedOrder) {
        return null;
    }

    const isTableRow = columnsCount !== undefined;
    const rootClassNames = [
        'fulfillment-section-header',
        isTableRow ? 'table-row' : 'fulfillment-section-header--standalone',
        isExpanded ? 'is-expanded' : '',
    ].filter(Boolean).join(' ');

    return (
        <div className={rootClassNames} data-testid="fulfillment-section-header">
            <div
                className="fulfillment-section-header__cell"
                style={isTableRow ? { gridColumn: `span ${columnsCount}` } : undefined}
            >
                <button
                    type="button"
                    className="fulfillment-section-header__trigger"
                    aria-expanded={isExpanded}
                    onClick={onToggle}
                >
                    <span className="fulfillment-section-header__chevron">
                        <Icon color={'var(--color-primary-2)'} Component={IconNext} />
                    </span>
                    <Icon color={'var(--color-primary-2)'} Component={IconPackage} />
                    <FontBody2 color={FONT_COLOR.PRIMARY2} variant={FONT_VARIANT.BOLD}>
                        {getTranslation('FULFILLMENT_SECTION_TITLE', PAGE_SCOPE, [String(draft.index)])}
                    </FontBody2>
                    <FulfillmentSectionMetadata draft={draft} />
                </button>
                {draft.isRMACreated &&
                    <span className="fulfillment-section-header__created" data-testid="fulfillment-section-created">
                        <FontBody3 color={FONT_COLOR.PRIMARY2}>
                            {getTranslation('FULFILLMENT_RMA_CREATED', PAGE_SCOPE)}
                        </FontBody3>
                    </span>
                }
            </div>
        </div>
    );
});

export default FulfillmentSectionHeader;
